import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch, faCalendarAlt, faImages } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";
import { bytesToSize } from '../utils/bytesSize';

const EventSidebar = ({ events, searchTerm, setSearchTerm, onSelectEvent, selectedEvent, onNewEvent }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <div style={{ padding: "16px 12px", display: "flex", flexDirection: "column", gap: "12px" }}>

      {/* Search */}
      <div style={{
        display: "flex", alignItems: "center", gap: "8px",
        padding: "8px 12px", borderRadius: "10px",
        border: "1px solid #e5e7eb", background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}>
        <FontAwesomeIcon icon={faSearch} style={{ color: "#9ca3af", fontSize: "12px", flexShrink: 0 }} />
        <input
          type="text"
          placeholder={t("events_admin.search_placeholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            border: "none", outline: "none", background: "transparent",
            fontSize: "13px", color: "#374151", width: "100%",
          }}
        />
      </div>

      {/* Event list */}
      <div style={{ maxHeight: "65vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
        {events.map((ev) => {
          const isSelected = selectedEvent?.event_id === ev.event_id;
          return (
            <div
              key={ev.event_id}
              onClick={() => onSelectEvent(ev)}
              style={{
                padding: "10px 12px",
                borderRadius: "12px",
                border: `1px solid ${isSelected ? "#c7d2fe" : "#f0f0f0"}`,
                background: isSelected ? "#eef2ff" : "#fafafa",
                cursor: "pointer",
                transition: "all 0.15s",
                borderLeft: isSelected ? "3px solid #6366f1" : "3px solid transparent",
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
            >
              {/* Event name */}
              <div style={{
                fontSize: "13px", fontWeight: 600,
                color: isSelected ? "#4338ca" : "#111827",
                marginBottom: "6px",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {ev.name}
              </div>

              {/* Date */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: ev.storage ? "4px" : 0 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "6px",
                  background: isSelected ? "#e0e7ff" : "#f3f4f6",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ color: isSelected ? "#6366f1" : "#9ca3af", fontSize: "10px" }} />
                </div>
                <span style={{ fontSize: "11px", color: isSelected ? "#6366f1" : "#6b7280", fontWeight: 500 }}>
                  {ev.date}
                </span>
              </div>

              {/* Storage */}
              {ev.storage && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "6px",
                    background: isSelected ? "#e0e7ff" : "#f3f4f6",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <FontAwesomeIcon icon={faImages} style={{ color: isSelected ? "#6366f1" : "#9ca3af", fontSize: "10px" }} />
                  </div>
                  <span style={{ fontSize: "11px", color: isSelected ? "#818cf8" : "#9ca3af" }}>
                    {ev.storage.total_images} {t("events_admin.photos", { count: ev.storage.total_images })}
                    {" · "}
                    {bytesToSize(ev.storage.used_memory)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer: new event or upgrade */}
      {(!user?.subscription?.max_events || events.length < user.subscription.max_events) ? (
        <button
          onClick={onNewEvent}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
            width: "100%", padding: "9px 0",
            borderRadius: "10px",
            border: "1px dashed #c7d2fe",
            background: "#fafafa",
            color: "#6366f1",
            cursor: "pointer",
            fontSize: "12px", fontWeight: 600,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#eef2ff"; e.currentTarget.style.borderColor = "#a5b4fc"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#fafafa"; e.currentTarget.style.borderColor = "#c7d2fe"; }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: "11px" }} />
          {t("events_admin.new_event")}
        </button>
      ) : (
        <div style={{
          padding: "10px 12px", borderRadius: "10px",
          border: "1px solid #fde68a", background: "#fffbeb",
          textAlign: "center",
        }}>
          <div style={{ fontSize: "11px", color: "#92400e", fontWeight: 500, marginBottom: "6px" }}>
            {t("events_admin.max_events_reached", { count: user.subscription?.max_events })}
          </div>
          <a
            href="/plans"
            style={{
              fontSize: "11px", fontWeight: 700,
              color: "#6366f1", textDecoration: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
            onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
          >
            {t("events_admin.upgrade_plan")}
          </a>
        </div>
      )}
    </div>
  );
};

export default EventSidebar;
